import React, { useState, useRef, useCallback } from "react";
import { toFile, fromFile, toText, fileName } from "./binderFile";

/**
 * Taking a binder out of the app and putting one back.
 *
 * Import replaces the whole binder, so it asks first and says exactly what it
 * is about to do — how many cards are coming in and that the current layout
 * goes. An overwrite you did not expect is the worst thing this could do, and
 * it is the easy thing to build by accident.
 */

export default function BinderFiles({ binder, onReplace }) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(null);   // parsed and waiting for a yes
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const save = useCallback(() => {
    const blob = new Blob([JSON.stringify(toFile(binder), null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName(binder);
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Object URLs hold the blob until they are given back.
    setTimeout(() => URL.revokeObjectURL(url), 0);
    setNote(`Saved ${fileName(binder)}`);
    setError("");
  }, [binder]);

  const copy = useCallback(async () => {
    const text = toText(binder);
    if (!text) { setError("Nothing in the binder to copy yet."); return; }
    try {
      await navigator.clipboard.writeText(text);
      setNote("Card list copied — paste it into Price a stack to value it.");
      setError("");
    } catch {
      setError("Could not reach the clipboard. Save the file instead.");
    }
  }, [binder]);

  const take = useCallback((raw) => {
    const result = fromFile(raw);
    if (result.error) { setError(result.error); setPending(null); return; }
    setError("");
    setNote("");
    setPending(result);
  }, []);

  const onFile = useCallback(
    (file) => {
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => take(String(reader.result || ""));
      reader.onerror = () => setError("That file could not be read.");
      reader.readAsText(file);
    },
    [take]
  );

  const confirm = useCallback(() => {
    onReplace(pending.binder);
    setNote(`Loaded ${pending.cards} card${pending.cards === 1 ? "" : "s"}.`);
    setPending(null);
  }, [pending, onReplace]);

  if (!open) {
    return (
      <button type="button" className="btn btn-secondary bnd-files-open" onClick={() => setOpen(true)}>
        Import / export
      </button>
    );
  }

  return (
    <div className="bnd-files">
      <div className="bnd-files-head">
        <span>Import / export</span>
        <button type="button" onClick={() => { setOpen(false); setPending(null); setError(""); setNote(""); }} aria-label="Close import and export">
          ×
        </button>
      </div>

      <div className="bnd-files-row">
        <button type="button" className="btn btn-secondary" onClick={save}>
          Save as a file
        </button>
        <button type="button" className="btn btn-secondary" onClick={copy}>
          Copy card list
        </button>
        <button type="button" className="btn btn-secondary" onClick={() => fileRef.current?.click()}>
          Load a file
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="application/json,.json"
          className="bnd-files-input"
          aria-label="Choose a binder file"
          onChange={(e) => { onFile(e.target.files?.[0]); e.target.value = ""; }}
        />
      </div>

      <label className="bnd-files-paste">
        or paste a binder here
        <textarea
          rows={3}
          spellCheck={false}
          placeholder='{"format":"pokepricer-binder", …}'
          onChange={(e) => { const v = e.target.value.trim(); if (v) take(v); }}
        />
      </label>

      {/* Named before it happens, not reported after. */}
      {pending && (
        <div className="bnd-files-confirm">
          <p>
            <strong>{pending.cards} card{pending.cards === 1 ? "" : "s"}</strong> on a{" "}
            {pending.binder.cols}×{pending.binder.rows} page, {pending.binder.pages} pages
            {pending.skipped > 0 && ` (${pending.skipped} entries skipped)`}. This replaces
            everything in “{binder.name}”.
          </p>
          <div className="bnd-files-row">
            <button type="button" className="btn btn-primary" onClick={confirm}>
              Replace the binder
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => setPending(null)}>
              Keep what I have
            </button>
          </div>
        </div>
      )}

      {error && <p className="msg-error">{error}</p>}
      {note && !error && <p className="bnd-files-note">{note}</p>}
    </div>
  );
}
